import React, { useEffect, useState } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

// Clipboard helper for non-HTTPS origins (same pattern as ChannelManagerSettings)
const copyText = async (text) => {
  const s = String(text ?? '');
  try { if (navigator?.clipboard?.writeText) { await navigator.clipboard.writeText(s); return true; } } catch {}
  try {
    const ta = document.createElement('textarea');
    ta.value = s; ta.setAttribute('readonly', '');
    ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
};

// Free QR image service (goqr.me). Used only to render a preview + printable
// image on the admin screen — not embedded in the actual printed sticker
// (admin downloads and prints from the image directly).
const qrImageSrc = (url, size = 280) =>
  `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=8&data=${encodeURIComponent(url || '')}`;

function KotTableMaster({ token, selectedProperty, selectedOutletId = '' }) {
  const [outlets, setOutlets] = useState([]);
  const [scope, setScope] = useState({
    chain_id: selectedProperty?.chain_id || '',
    property_id: selectedProperty?.id || '',
    outlet_id: selectedOutletId || ''
  });
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ table_no: '', capacity: 2, zone: '', is_active: true });
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  // QR state — { row, qr: { qr_url, qr_code, ... } | null, loading, rotating }
  const [qrModal, setQrModal] = useState(null);
  const [qrCopied, setQrCopied] = useState(false);
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const scopeHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': scope.chain_id,
    'x-property-id': scope.property_id,
    'x-outlet-id': scope.outlet_id
  });

  const loadOutlets = async (chainId, propertyId) => {
    if (!chainId || !propertyId) return;
    try {
      const res = await fetch(`/api/v1/core/outlets?property_id=${encodeURIComponent(propertyId)}`, {
        headers: { Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load outlets');
      const all = Array.isArray(data) ? data : [];
      const list = all.filter(o => String(o.outlet_type || '').toUpperCase() !== 'BAR' || o.module_kot_enabled === true);
      setOutlets(list);
      if (list.length) {
        const match = selectedOutletId && list.find((o) => String(o.id) === String(selectedOutletId));
        setScope((prev) => ({ ...prev, outlet_id: match ? match.id : list[0].id }));
      } else {
        setScope((prev) => ({ ...prev, outlet_id: '' }));
        setError('No outlet found for selected property. Please create outlet in KOT -> KOT Outlet Master first.');
      }
    } catch (err) {
      setError(err?.message || 'Network error while loading outlets');
    }
  };

  const loadRows = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setRows([]);
    try {
      const res = await fetch('/api/v1/kot/masters/tables?all=1', { headers: scopeHeaders() });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Failed to load KOT tables');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading KOT tables');
    }
  };

  useEffect(() => {
    if (selectedProperty?.chain_id && selectedProperty?.id) {
      setScope((prev) => ({ ...prev, chain_id: selectedProperty.chain_id, property_id: selectedProperty.id, outlet_id: selectedOutletId || '' }));
    } else {
      setScope({ chain_id: '', property_id: '', outlet_id: '' });
      setRows([]);
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (selectedOutletId && outlets.some((o) => String(o.id) === String(selectedOutletId))) {
      setScope((prev) => ({ ...prev, outlet_id: selectedOutletId }));
    }
  }, [selectedOutletId]);

  useEffect(() => { if (scope.chain_id && scope.property_id) loadOutlets(scope.chain_id, scope.property_id); }, [scope.chain_id, scope.property_id, token]);
  useEffect(() => { loadRows(); }, [scope.outlet_id]);

  const saveRow = async () => {
    setError('');
    if (!scope.chain_id || !scope.property_id) return setError('Select hotel property first from header');
    if (!scope.outlet_id) return setError('Select outlet. If outlet is not listed, create one in KOT -> KOT Outlet Master.');
    if (!String(form.table_no || '').trim()) return setError('Table No is required');
    const capacity = Number(form.capacity || 0);
    if (Number.isNaN(capacity) || capacity <= 0) return setError('Capacity must be greater than zero');

    try {
      const res = await fetch(editingId ? `/api/v1/kot/masters/tables/${editingId}` : '/api/v1/kot/masters/tables', {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          table_no: form.table_no,
          capacity,
          zone: form.zone || null,
          is_active: form.is_active !== false
        })
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Save failed');
      setMessage(editingId ? 'Table updated successfully' : 'Table created successfully');
      setEditingId('');
      setForm({ table_no: '', capacity: 2, zone: '', is_active: true });
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const editRow = (row) => {
    setEditingId(row.id);
    setForm({
      table_no: row.table_no || '',
      capacity: Number(row.capacity || 2),
      zone: row.zone || '',
      is_active: row.is_active !== false
    });
    setError('');
    setMessage('');
    scrollToForm();
  };

  const openQrModal = async (row) => {
    setError('');
    setQrCopied(false);
    setQrModal({ row, qr: null, loading: true, rotating: false });
    try {
      const res = await fetch(`/api/v1/kot/tables/${row.id}/qr`, { headers: scopeHeaders() });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to load QR'); setQrModal(null); return; }
      setQrModal({ row, qr: data.qr, loading: false, rotating: false });
    } catch {
      setError('Network error');
      setQrModal(null);
    }
  };

  const rotateQr = async () => {
    if (!qrModal?.row) return;
    setQrModal(m => ({ ...m, rotating: true }));
    try {
      const res = await fetch(`/api/v1/kot/tables/${qrModal.row.id}/qr/rotate`, {
        method: 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Rotate failed'); setQrModal(m => ({ ...m, rotating: false })); return; }
      setQrCopied(false);
      setQrModal(m => ({ ...m, qr: data.qr, rotating: false }));
    } catch {
      setError('Network error');
      setQrModal(m => m ? { ...m, rotating: false } : null);
    }
  };

  const printQr = () => {
    if (!qrModal?.qr?.qr_url) return;
    const url = qrModal.qr.qr_url;
    const tableLabel = qrModal.row?.table_no || '';
    const outletLabel = outlets.find(o => o.id === qrModal.row?.outlet_id)?.name || '';
    const html = `
      <html><head><title>Table QR — ${tableLabel}</title>
      <style>
        body { font-family: Arial, sans-serif; text-align: center; padding: 24px; }
        h1 { margin: 0 0 6px; font-size: 28px; }
        h2 { margin: 0 0 14px; font-weight: 500; color: #555; font-size: 16px; }
        .url { word-break: break-all; font-family: monospace; font-size: 11px; color: #666; margin-top: 18px; }
        img { width: 380px; height: 380px; }
        .ask { margin-top: 10px; font-size: 14px; color: #333; }
      </style></head><body>
        <h1>${outletLabel}</h1>
        <h2>Scan to order — Table ${tableLabel}</h2>
        <img src="${qrImageSrc(url, 380)}" alt="QR code" />
        <div class="ask">Scan with your phone camera.</div>
        <div class="url">${url}</div>
        <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
      </body></html>`;
    const w = window.open('', '_blank', 'width=520,height=760');
    if (!w) { setError('Enable popups to print the QR'); return; }
    w.document.write(html);
    w.document.close();
  };

  const deactivateRow = async (id, tableNo) => {
    if (!window.confirm(`Deactivate table "${tableNo}"?`)) return;
    setError('');
    try {
      const res = await fetch(`/api/v1/kot/masters/tables/${id}`, {
        method: 'DELETE',
        headers: scopeHeaders()
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Deactivate failed');
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const inputStyle = { padding: '0.55rem', border: '1px solid #dcdde1', borderRadius: '6px' };
  const btnStyle = { padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>KOT Table Master</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="filter-bar">
        <h3>Property & Outlet</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '0.7rem' }}>
          <input
            value={selectedProperty ? `${selectedProperty.code || ''} ${selectedProperty.name || ''}`.trim() : 'No property selected'}
            readOnly
            style={{ ...inputStyle, background: '#ecf0f1' }}
          />
          <select value={scope.outlet_id} onChange={(e) => setScope((prev) => ({ ...prev, outlet_id: e.target.value }))} style={inputStyle}>
            <option value="">Select Outlet</option>
            {outlets.map((o) => <option key={o.id} value={o.id}>{o.code} - {o.name}</option>)}
          </select>
        </div>
      </div>

      <div className="filter-bar" ref={formRef} style={editHighlightStyle(editHighlight)}>
        <h3>{editingId ? 'Edit Table' : 'Add Table'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.7rem' }}>
          <input placeholder="Table No" value={form.table_no} onChange={(e) => setForm({ ...form, table_no: e.target.value })} style={inputStyle} />
          <input type="number" min="1" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} style={inputStyle} />
          <input placeholder="Zone (optional)" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} style={inputStyle} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active
          </label>
        </div>
        <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={saveRow} className="btn-primary" style={btnStyle}>{editingId ? 'Update' : 'Add'}</button>
          {editingId && (
            <button onClick={() => { setEditingId(''); setForm({ table_no: '', capacity: 2, zone: '', is_active: true }); }} className="btn-muted" style={btnStyle}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="filter-bar">
        <h3>KOT Tables</h3>
        {rows.length === 0 ? (
          <p>No tables found</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Table No</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Capacity</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Zone</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Active</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>QR</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #dcdde1' }}>
                  <td style={{ padding: '0.6rem' }}>{r.table_no}</td>
                  <td style={{ padding: '0.6rem' }}>{r.capacity}</td>
                  <td style={{ padding: '0.6rem' }}>{r.zone || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.is_active ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '0.6rem' }}>
                    <button onClick={() => openQrModal(r)} style={{ ...btnStyle, background: '#7c3aed', color: '#fff' }}>QR Code</button>
                  </td>
                  <td style={{ padding: '0.6rem', display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => editRow(r)} className="btn-primary" style={btnStyle}>Edit</button>
                    <button onClick={() => deactivateRow(r.id, r.table_no)} className="btn-danger" style={btnStyle}>Deactivate</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── QR Code modal ── */}
      {qrModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div style={{ width: 'min(520px, 96vw)', maxHeight: '92vh', overflow: 'auto', background: '#fff', borderRadius: 14, boxShadow: '0 24px 48px rgba(0,0,0,0.25)' }}>
            <div style={{ background: '#4c1d95', color: '#fff', padding: '0.9rem 1.25rem', borderRadius: '14px 14px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.02rem' }}>Table QR — {qrModal.row?.table_no}</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.85 }}>Customer scans → browser opens the self-order landing.</div>
              </div>
              <button onClick={() => setQrModal(null)}
                style={{ border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', width: 30, height: 30, borderRadius: 8, cursor: 'pointer', fontSize: '1rem', fontWeight: 700 }}>✕</button>
            </div>
            <div style={{ padding: '1rem 1.25rem' }}>
              {qrModal.loading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading QR…</div>
              ) : !qrModal.qr ? (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ color: '#64748b', marginBottom: '1rem' }}>No QR has been generated for this table yet.</div>
                  <button onClick={rotateQr} disabled={qrModal.rotating}
                    style={{ ...btnStyle, background: '#4c1d95', color: '#fff', padding: '0.7rem 1.2rem', fontWeight: 700 }}>
                    {qrModal.rotating ? 'Generating…' : 'Generate QR'}
                  </button>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.8rem' }}>
                    <img src={qrImageSrc(qrModal.qr.qr_url)} alt="QR code preview" style={{ width: 280, height: 280, border: '1px solid #e5e7eb', borderRadius: 8 }} />
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 4 }}>QR code: <b style={{ color: '#1f2937', fontFamily: 'monospace' }}>{qrModal.qr.qr_code}</b></div>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 4 }}>Salt: <b style={{ color: '#1f2937', fontFamily: 'monospace' }}>{qrModal.qr.salt}</b>  ·  Rotated: <b>{qrModal.qr.last_rotated_at ? new Date(qrModal.qr.last_rotated_at).toLocaleString() : '—'}</b></div>
                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginTop: '0.6rem', padding: '0.45rem 0.6rem', background: '#f1f5f9', borderRadius: 6, fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all' }}>
                    <span style={{ flex: 1 }}>{qrModal.qr.qr_url}</span>
                    <button onClick={async () => { const ok = await copyText(qrModal.qr.qr_url); setQrCopied(ok); setTimeout(() => setQrCopied(false), 1500); }}
                      style={{ ...btnStyle, padding: '0.25rem 0.5rem', background: '#2563eb', color: '#fff', fontSize: '0.75rem' }}>{qrCopied ? 'Copied!' : 'Copy'}</button>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem', flexWrap: 'wrap' }}>
                    <button onClick={() => setQrModal(null)} style={{ ...btnStyle, background: '#e2e8f0', color: '#334155' }}>Close</button>
                    <button onClick={printQr} style={{ ...btnStyle, background: '#16a34a', color: '#fff' }}>Print / Save</button>
                    <button onClick={rotateQr} disabled={qrModal.rotating} title="Invalidates the old sticker — use only if the QR was lost or compromised."
                      style={{ ...btnStyle, background: '#dc2626', color: '#fff' }}>
                      {qrModal.rotating ? 'Rotating…' : 'Rotate QR'}
                    </button>
                  </div>
                  <div style={{ fontSize: '0.72rem', color: '#78350f', marginTop: '0.7rem' }}>
                    Rotating invalidates the current sticker — reprint after rotation so the new QR is on the table.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default KotTableMaster;
